import type { JSONRPC } from './types'
import type { Arrayable, Awaitable } from './typings/utils'
import { RpcException } from './errors/rpc-exception'
import { TimeoutException } from './errors/timeout-exception'
import { isRequest, isResponse } from './typings/jsonrpc'

export interface NotificationOptions<TParams extends unknown[] | Record<string, unknown> = unknown[] | Record<string, unknown>> extends Pick<JSONRPC.Request<TParams>, 'method' | 'params'> {
  jsonrpc?: JSONRPC.Request['jsonrpc']
  id?: JSONRPC.Request['id']
}

export interface SendRequestOptions<TParams extends unknown[] | Record<string, unknown> = unknown[] | Record<string, unknown>> extends Omit<NotificationOptions<TParams>, 'id'> {
  id: Exclude<JSONRPC.Request['id'], null>
}

export interface ConnectionAdapter {
  /**
   * How to send a event.
   *
   * This method is expected to return a promise that resolves when the event is sent.
   *
   * @param request
   */
  sendEvent(request: Arrayable<JSONRPC.Event>): Awaitable<void>
  /**
   * Callback to handle responses.
   *
   * This method is expected to be called when a response is received.
   *
   * @param callback
   */
  onEvent(callback: (response: Arrayable<JSONRPC.Event>) => void): Awaitable<void>
}

export interface TimeoutOptions {
  /**
   * Timeout in milliseconds for the request.
   * If not provided, defaults to 5000ms (5 seconds).
   */
  timeout?: number
}

export interface ConnectionOptions<TFunctions extends JSONRPC.Dictionary<(...args: unknown[]) => unknown> = JSONRPC.Dictionary<(...args: unknown[]) => unknown>> extends TimeoutOptions {
  /**
   * The adapter to use for sending requests and handling responses.
   */
  adapter: ConnectionAdapter
  /**
   * Call functions when the request is sent.
   */
  functions?: TFunctions
}

export interface Connection {
  /**
   * Sends a notification.
   *
   * @param notification - The notification to send.
   */
  sendNotification<TParams extends unknown[] | JSONRPC.Dictionary<unknown>>(notification: Arrayable<NotificationOptions<TParams>>, options?: TimeoutOptions | number): Promise<void>
  /**
   * Sends a request and waits for a response.
   *
   * @param request - The request to send.
   * @returns A promise that resolves with the response.
   */
  sendRequest<TParams extends unknown[] | JSONRPC.Dictionary<unknown>, R = unknown, E = unknown, TRequest extends Arrayable<SendRequestOptions<TParams>> = Arrayable<SendRequestOptions<TParams>>>(request: TRequest, options?: TimeoutOptions | number): Promise<TRequest extends any[] ? JSONRPC.Response<R, E>[] : JSONRPC.Response<R, E>>
  /**
   * Callback to handle responses.
   *
   * This method is expected to be called when a response is received.
   *
   * @param callback
   */
  onResponse(callback: (response: Arrayable<JSONRPC.Response>) => Awaitable<unknown>): Awaitable<void>
  /**
   * Starts listening for requests and handles them.
   */
  listen(): Promise<void>
  /**
   * Get current underlying adapter.
   */
  getAdapter(): ConnectionAdapter
}

class ConnectionImpl<TFunctions extends JSONRPC.Dictionary<(...args: unknown[]) => unknown>> implements Connection {
  constructor(private options: ConnectionOptions<TFunctions>) {}

  private get timeout(): number {
    return this.options.timeout ?? 5000
  }

  public static createThrowableTimeout(operationType: 'sendRequest' | 'sendNotification', timeout: number): Promise<void> {
    return new Promise<void>((_, reject) => {
      const timer = setTimeout(() => {
        clearTimeout(timer)
        reject(new TimeoutException(operationType))
      }, timeout)
    })
  }

  public sendNotification(notification: Arrayable<NotificationOptions>, options?: TimeoutOptions | number): Promise<void> {
    return Promise.race([
      Array.isArray(notification)
        ? this.options.adapter.sendEvent(notification.map(notify => ({
            ...notify,
            jsonrpc: notify.jsonrpc ?? '2.0',
            id: notify.id ?? null,
          })))
        : this.options.adapter.sendEvent({
            ...notification,
            jsonrpc: notification.jsonrpc ?? '2.0',
            id: notification.id ?? null,
          }),
      ConnectionImpl.createThrowableTimeout('sendNotification', typeof options === 'number' ? options : (options?.timeout ?? this.timeout)),
    ])
  }

  public async sendRequest<TParams extends unknown[] | JSONRPC.Dictionary<unknown>, R = unknown, E = unknown, TRequest extends Arrayable<SendRequestOptions<TParams>> = Arrayable<SendRequestOptions<TParams>>>(request: TRequest, options?: TimeoutOptions | number): Promise<TRequest extends any[] ? JSONRPC.Response<R, E>[] : JSONRPC.Response<R, E>> {
    const resolvedRequest = (Array.isArray(request)
      ? request.map(req => ({
          ...req,
          jsonrpc: req.jsonrpc ?? '2.0',
          id: req.id ?? null,
        }))
      : {
          ...request,
          jsonrpc: request.jsonrpc ?? '2.0',
          id: request.id ?? null,
        }) as TRequest extends any[] ? JSONRPC.Request<TParams>[] : JSONRPC.Request<TParams>

    const listenPromise = new Promise<any>(resolve => (
      this.options.adapter.onEvent((response) => {
        if (Array.isArray(request) && Array.isArray(response) && response.some(res => request.some(req => req.id === res.id))) {
          resolve(response)
        }

        if (!Array.isArray(request) && !Array.isArray(response) && request.id === response.id)
          resolve(response)
      })
    ))
    this.options.adapter.sendEvent(resolvedRequest)
    return Promise.race([
      listenPromise,
      ConnectionImpl.createThrowableTimeout('sendRequest', typeof options === 'number' ? options : (options?.timeout ?? this.timeout)),
    ])
  }

  onResponse(callback: (response: Arrayable<JSONRPC.Response>) => void): Awaitable<void> {
    return this.options.adapter.onEvent((response) => {
      if (Array.isArray(response)) {
        if (response.every(res => isResponse(res))) {
          callback(response)
        }
      }
      else if (isResponse(response)) {
        callback(response)
      }
    })
  }

  private convertParamsToArray(params: unknown[] | JSONRPC.Dictionary<unknown> | undefined): unknown[] {
    if (Array.isArray(params))
      return params
    else if (typeof params === 'object' && params !== null)
      return Object.values(params)
    else
      return []
  }

  getAdapter(): ConnectionAdapter {
    return this.options.adapter
  }

  async listen(): Promise<void> {
    await this.options.adapter.onEvent(async (resOrReq) => {
      if (Array.isArray(resOrReq)) {
        if (resOrReq.every(reqs => isRequest(reqs))) {
          const res = await Promise.all(
            resOrReq.map(async (req): Promise<JSONRPC.Response> => {
              try {
                return {
                  jsonrpc: '2.0',
                  id: req.id,
                  result: await this.options.functions?.[req.method]?.(...this.convertParamsToArray(req.params)) ?? null,
                }
              }
              catch (error) {
                return {
                  jsonrpc: '2.0',
                  id: req.id,
                  error: error instanceof RpcException
                    ? error
                    : {
                        code: -32603, // Internal error
                        message: String(error),
                      },
                }
              }
            }),
          )
          await this.options.adapter.sendEvent(res)
        }
      }
      else if (isRequest(resOrReq)) {
        try {
          await this.options.adapter.sendEvent({
            jsonrpc: '2.0',
            id: resOrReq.id,
            result: await this.options.functions?.[resOrReq.method]?.(...this.convertParamsToArray(resOrReq.params)) ?? null,
          })
        }
        catch (error) {
          await this.options.adapter.sendEvent({
            jsonrpc: '2.0',
            id: resOrReq.id,
            error: error instanceof RpcException
              ? error
              : {
                  code: -32603, // Internal error
                  message: String(error),
                },
          })
        }
      }
    })
  }
}

export function createConnection<TFunctions extends JSONRPC.Dictionary<(...args: unknown[]) => unknown> = JSONRPC.Dictionary<(...args: unknown[]) => unknown>>(options: ConnectionOptions): Connection {
  return new ConnectionImpl<TFunctions>(options as ConnectionOptions<TFunctions>)
}
